import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import type { ReactNode } from "react";
import { PriceInput } from "@/components/ui/input/price-input";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";

type MoneyFieldBaseProps = {
  label?: ReactNode;
  optional?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
  placeholder?: string;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner PriceInput. */
  inputClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  prefixClassName?: string;
  suffixClassName?: string;
  clearable?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
  /**
   * When true (default), the form value is coerced to `number` on change and
   * stringified for display. When false, the raw string is stored and emitted.
   */
  coerceToNumber?: boolean;
};

export type MoneyFieldProps = MoneyFieldBaseProps & FieldBindingProps;

export function MoneyField(props: MoneyFieldProps) {
  const {
    label,
    optional,
    disabled,
    allowNegative,
    placeholder,
    className,
    inputClassName,
    prefix,
    suffix,
    prefixClassName,
    suffixClassName,
    clearable,
    autoFocus,
    onBlur,
    coerceToNumber = true,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<number | string | null> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={disabled}
        >
          <PriceInput
            value={field.state.value == null ? "" : String(field.state.value)}
            onChange={(value) => {
              // PriceInput's own `onChange` intersects the DOM input handler it
              // spreads onto, so the parameter widens to include a ChangeEvent.
              // At runtime it only ever emits the validated raw string.
              const raw =
                typeof value === "string" || typeof value === "number"
                  ? String(value)
                  : "";
              const next = coerceToNumber
                ? raw === ""
                  ? null
                  : Number(raw)
                : raw;
              field.handleChange(next);
            }}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
            allowNegative={allowNegative}
            placeholder={placeholder}
            className={inputClassName}
            prefix={prefix}
            suffix={suffix}
            prefixClassName={prefixClassName}
            suffixClassName={suffixClassName}
            clearable={clearable}
            autoFocus={autoFocus}
            disabled={disabled}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
